import { useState, useEffect } from 'preact/hooks';
import { useAuth } from '../lib/auth';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.unplug-app.com';

export function UpgradePage() {
  const { subscription, workspace } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [error, setError] = useState(null);

  const isTrialing = subscription?.status === 'trialing';
  const isActive = subscription?.status === 'active';
  const currentPlan = subscription?.plan || 'trial';

  useEffect(() => {
    fetchPlans();
  }, []);

  async function fetchPlans() {
    try {
      const res = await fetch(`${API_URL}/v3/plans`);
      if (res.ok) {
        const data = await res.json();
        setPlans(data.plans || []);
      }
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    } finally {
      setLoadingPlans(false);
    }
  }

  function handleUpgrade(planKey) {
    // Navigate to checkout page with selected plan
    window.location.href = `/checkout?plan=${planKey}`;
  }

  function getPlanCardClass(plan) {
    let classes = 'plan-card';
    if (plan.key === 'pro') classes += ' featured';
    if (plan.key === currentPlan && (isActive || (isTrialing && plan.key === 'free'))) {
      classes += ' current';
    }
    return classes;
  }

  function isCurrentPlan(plan) {
    if (isTrialing && plan.key === 'free') return true;
    if (isActive && plan.key === currentPlan) return true;
    return false;
  }

  function canUpgrade(plan) {
    if (plan.key === 'free') return false;
    if (isCurrentPlan(plan)) return false;
    return true;
  }

  function getSeatsLabel(seatsLimit) {
    if (seatsLimit === 0) return 'Unlimited users';
    return `Up to ${seatsLimit} users`;
  }

  if (loadingPlans) {
    return (
      <div class="upgrade-page">
        <div class="loading-screen">
          <div class="spinner" />
          <p>Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div class="upgrade-page">
      <div class="upgrade-header">
        <h1>Choose Your Plan</h1>
        <p>Simple, transparent pricing. No per-user fees.</p>
      </div>

      {error && (
        <div class="error-banner">
          {error}
        </div>
      )}

      <div class="plans-container">
        {plans.map(plan => (
          <div key={plan.key} class={getPlanCardClass(plan)}>
            {plan.key === 'pro' && <div class="featured-badge">Most Popular</div>}
            <div class="plan-header">
              <h2>{plan.name}</h2>
              <div class="plan-price">
                <span class="currency">EUR</span>
                <span class="amount">{plan.price_monthly}</span>
                <span class="period">/month</span>
              </div>
            </div>
            <div class="plan-description">
              {plan.description || `${getSeatsLabel(plan.seats_limit)}`}
            </div>
            <ul class="plan-features">
              <li>
                <span class="check">&#10003;</span>
                {getSeatsLabel(plan.seats_limit)}
              </li>
              {plan.features && plan.features.map(feature => (
                <li key={feature.key}>
                  <span class="check">&#10003;</span>
                  {feature.name}
                </li>
              ))}
            </ul>
            <div class="plan-action">
              {isCurrentPlan(plan) ? (
                <span class="current-badge">Current Plan</span>
              ) : canUpgrade(plan) ? (
                <button
                  class={`btn ${plan.key === 'pro' ? 'btn-primary' : 'btn-secondary'} btn-lg`}
                  onClick={() => handleUpgrade(plan.key)}
                  disabled={loading === plan.key}
                >
                  {loading === plan.key ? 'Loading...' : `Upgrade to ${plan.name}`}
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      <div class="pricing-footer">
        <div class="guarantee">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <div>
            <strong>30-day money-back guarantee</strong>
            <p>Not satisfied? Get a full refund, no questions asked.</p>
          </div>
        </div>

        <div class="faq">
          <h3>Frequently Asked Questions</h3>

          <details>
            <summary>Can I change plans later?</summary>
            <p>Yes, you can upgrade or downgrade at any time. Changes take effect immediately and are prorated.</p>
          </details>

          <details>
            <summary>What payment methods do you accept?</summary>
            <p>We accept all major credit cards (Visa, Mastercard, American Express) through our secure payment provider Stripe.</p>
          </details>

          <details>
            <summary>Is there a contract or commitment?</summary>
            <p>No, all plans are month-to-month. You can cancel anytime with no penalties.</p>
          </details>

          <details>
            <summary>What happens when I reach my user limit?</summary>
            <p>You'll be prompted to upgrade to a higher plan. Existing users will continue to have access.</p>
          </details>
        </div>

        <p class="contact-note">
          Have questions? Contact us at{' '}
          <a href="mailto:helpdesk@vanilla.net.mt">helpdesk@vanilla.net.mt</a>
        </p>
      </div>

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .upgrade-page {
    max-width: 1200px;
    margin: 0 auto;
  }

  .upgrade-header {
    text-align: center;
    margin-bottom: 3rem;
  }

  .upgrade-header h1 {
    font-size: 2rem;
    color: var(--gray-900);
    margin-bottom: 0.5rem;
    font-weight: 700;
  }

  .upgrade-header p {
    font-size: 1.125rem;
    color: var(--gray-500);
  }

  .loading-screen {
    text-align: center;
    padding: 4rem 2rem;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--gray-200);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 1rem;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .error-banner {
    background: #fef2f2;
    color: var(--danger);
    padding: 1rem;
    border-radius: var(--bento-card-radius);
    margin-bottom: 2rem;
    text-align: center;
  }

  .plans-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: var(--bento-gap);
    margin-bottom: 3rem;
  }

  .plan-card {
    background: var(--bento-card-bg);
    border-radius: var(--bento-card-radius);
    box-shadow: var(--bento-card-shadow);
    padding: 2rem;
    border: 2px solid transparent;
    position: relative;
    display: flex;
    flex-direction: column;
  }

  .plan-card.featured {
    border-color: var(--primary);
    box-shadow: 0 10px 40px rgba(59, 130, 246, 0.15);
  }

  .plan-card.current {
    border-color: var(--success);
  }

  .featured-badge {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    background: var(--primary);
    color: white;
    padding: 0.25rem 1rem;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .plan-header {
    text-align: center;
    margin-bottom: 1rem;
  }

  .plan-header h2 {
    font-size: 1.5rem;
    color: var(--gray-900);
    margin-bottom: 0.5rem;
  }

  .plan-price {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.25rem;
  }

  .plan-price .currency {
    font-size: 1rem;
    color: var(--gray-500);
  }

  .plan-price .amount {
    font-size: 3rem;
    font-weight: 700;
    color: var(--gray-900);
  }

  .plan-price .period {
    font-size: 1rem;
    color: var(--gray-500);
  }

  .plan-description {
    text-align: center;
    color: var(--gray-500);
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--gray-200);
  }

  .plan-features {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem 0;
    flex: 1;
  }

  .plan-features li {
    padding: 0.5rem 0;
    color: var(--gray-600);
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .plan-features .check {
    color: var(--success);
    font-weight: bold;
  }

  .plan-action {
    text-align: center;
  }

  .current-badge {
    display: inline-block;
    background: #dcfce7;
    color: var(--success);
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-weight: 500;
  }

  .btn {
    display: inline-block;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 500;
    text-decoration: none;
    cursor: pointer;
    border: none;
    font-size: 1rem;
    transition: all 0.2s;
  }

  .btn-lg {
    padding: 1rem 2rem;
    font-size: 1.125rem;
  }

  .btn-primary {
    background: var(--primary);
    color: white;
  }

  .btn-primary:hover {
    background: var(--primary-dark);
  }

  .btn-secondary {
    background: var(--gray-900);
    color: white;
  }

  .btn-secondary:hover {
    background: var(--gray-800);
  }

  .btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .pricing-footer {
    max-width: 700px;
    margin: 0 auto;
  }

  .guarantee {
    display: flex;
    align-items: center;
    gap: 1rem;
    background: var(--bento-card-bg);
    box-shadow: var(--bento-card-shadow);
    padding: 1.5rem;
    border-radius: var(--bento-card-radius);
    margin-bottom: var(--bento-gap);
  }

  .guarantee svg {
    width: 40px;
    height: 40px;
    color: var(--success);
    flex-shrink: 0;
  }

  .guarantee strong {
    display: block;
    color: var(--gray-900);
    margin-bottom: 0.25rem;
  }

  .guarantee p {
    color: var(--gray-500);
    margin: 0;
    font-size: 0.875rem;
  }

  .faq {
    background: var(--bento-card-bg);
    box-shadow: var(--bento-card-shadow);
    border-radius: var(--bento-card-radius);
    padding: 1.5rem;
    margin-bottom: var(--bento-gap);
  }

  .faq h3 {
    font-size: 1.125rem;
    color: var(--gray-900);
    margin-bottom: 1rem;
  }

  .faq details {
    border-bottom: 1px solid var(--gray-200);
  }

  .faq summary {
    padding: 1rem 0;
    cursor: pointer;
    font-weight: 500;
    color: var(--gray-900);
  }

  .faq summary:hover {
    color: var(--primary);
  }

  .faq details p {
    padding: 0 0 1rem 0;
    color: var(--gray-500);
    margin: 0;
  }

  .contact-note {
    text-align: center;
    color: var(--gray-400);
  }

  .contact-note a {
    color: var(--primary);
  }

  @media (max-width: 768px) {
    .upgrade-header h1 {
      font-size: 1.5rem;
    }

    .plan-price .amount {
      font-size: 2.5rem;
    }
  }
`;
